/**
 * Utilitaires pour la validation des tokens
 */

// Regex pour valider le format d'un token (UUID v4 ou format personnalisé)
const TOKEN_REGEX = /^[a-f0-9]{8}-[a-f0-9]{4}-4[a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12}$/i;
const CUSTOM_TOKEN_REGEX = /^[a-zA-Z0-9_-]{20,}$/;

/**
 * Valide le format d'un token
 * @param token - Le token à valider
 * @returns true si le token est valide, false sinon
 */
export function isValidToken(token: string | null | undefined): token is string {
  if (!token || typeof token !== 'string') {
    return false;
  }

  // Validation basique : token non vide après trim
  const cleanToken = token.trim();
  if (cleanToken.length === 0) {
    return false;
  }

  // Validation optionnelle plus stricte pour les formats connus
  // Mais on accepte tous les tokens non vides pour la compatibilité
  return true;
}

/**
 * Nettoie et valide un token
 * @param token - Le token à nettoyer
 * @returns Le token nettoyé ou null si invalide
 */
export function sanitizeToken(token: string | null | undefined): string | null {
  if (!token) return null;
  
  const cleanToken = token.trim();
  return isValidToken(cleanToken) ? cleanToken : null;
}

/**
 * Récupère et valide un token depuis les paramètres d'URL
 * @param searchParams - Les paramètres de recherche de l'URL
 * @param pathToken - Le token depuis les paramètres de route
 * @returns Le token validé ou null
 */
export function getValidatedToken(
  searchParams: URLSearchParams, 
  pathToken?: string
): string | null {
  // Priorité aux paramètres de requête pour la navigation privée
  const queryToken = searchParams.get('token');
  const effectiveToken = queryToken || pathToken;
  
  return sanitizeToken(effectiveToken);
}

/**
 * Types pour la gestion des erreurs de token
 */
export enum TokenError {
  MISSING = 'TOKEN_MISSING',
  INVALID_FORMAT = 'TOKEN_INVALID_FORMAT',
  TOO_SHORT = 'TOKEN_TOO_SHORT',
  EXPIRED = 'TOKEN_EXPIRED'
}

/**
 * Valide un token avec des détails d'erreur
 * @param token - Le token à valider
 * @returns Objet avec le statut de validation et l'erreur éventuelle
 */
export function validateTokenWithDetails(token: string | null | undefined): {
  isValid: boolean;
  error?: TokenError;
  token?: string;
} {
  if (!token) {
    return { isValid: false, error: TokenError.MISSING };
  }

  if (typeof token !== 'string') {
    return { isValid: false, error: TokenError.INVALID_FORMAT };
  }

  const cleanToken = token.trim();
  
  if (cleanToken.length === 0) {
    return { isValid: false, error: TokenError.INVALID_FORMAT };
  }

  // Validation permissive : accepter tous les tokens non vides
  return { isValid: true, token: cleanToken };
}
